import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";

import { genericEnterKeyDown } from "utils/validation";

const Container = styled.div`
    width: 100%;
`;

const Txtarea = styled.textarea`
    padding: ${(props) => props.$padding || "6px 8px"};
    text-align: justify;
    width: 100%;
    max-width: ${(props) => props.$maxWidth || "1000px"};
    min-height: 8px;
    line-height: 1.5;
    font-size: ${(props) => props.fontsize || "1.4rem"};
    font-weight: ${(props) => props.fontweight || "400"};
    color: var(--color-SubBlack);
    background-color: ${({ $isEditable }) => ($isEditable ? "var(--color-Base1)" : "var(--color-White)")};
    border: ${({ $isEditable }) =>
        $isEditable ? "solid 1px var(--color-InputLine);" : "solid 1px var(--color-White)"};
    border-radius: 4px;
    outline: #dce1e7;
    resize: none;
    overflow: hidden;
    cursor: text;

    &:hover {
        background-color: ${({ $isEditable }) => ($isEditable ? "var(--color-Base1)" : "var(--color-Base1)")};
    }

    &::placeholder {
        color: var(--color-Grey2);
        font-weight: 400;
    }

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        display: initial;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--color-DarkKey);
    }

    &::-webkit-scrollbar-track {
        background-color: var(--color-ButtonHover4);
    }

    &:disabled {
        background-color: var(--color-DisabledInput);
        cursor: not-allowed;
    }
`;

function Textarea(props, ref) {
    const [isEditable, setIsEditable] = useState(false);
    const [oldValue, setOldValue] = useState("");
    const containerRef = useRef(null);
    const textareaRef = useRef(null);
    const resizeFrameRef = useRef(null);

    const resizeTextarea = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const nextHeight = `${textarea.scrollHeight + 4}px`;
        if (textarea.style.height !== nextHeight) {
            textarea.style.height = nextHeight;
        }
    }, []);

    const scheduleResizeTextarea = useCallback(() => {
        if (resizeFrameRef.current) {
            window.cancelAnimationFrame(resizeFrameRef.current);
        }
        resizeFrameRef.current = window.requestAnimationFrame(() => {
            resizeFrameRef.current = null;
            resizeTextarea();
        });
    }, [resizeTextarea]);

    const handleInputBlur = () => {
        if (props.value === "" && props.allowEmpty === false) {
            Alert("warn", "에러", "내용을 입력해주세요.", () => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            });
        } else {
            props.saveAct();
            setIsEditable(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            props.onChange({ target: { value: oldValue, name: props.name } });
            setIsEditable(false);
        } else {
            genericEnterKeyDown(e, () => {
                if (e.shiftKey) {
                    props.onChange(e.target.value + "\n");
                } else {
                    textareaRef.current.blur();
                }
                e.preventDefault();
            });
        }
    };

    useEffect(() => {
        if (isEditable) {
            setOldValue(props.value);
        }
    }, [isEditable, props.value]);

    useEffect(() => {
        scheduleResizeTextarea();
    }, [props.value, scheduleResizeTextarea]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined") return undefined;

        const observer = new ResizeObserver(() => {
            scheduleResizeTextarea();
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
            if (resizeFrameRef.current) {
                window.cancelAnimationFrame(resizeFrameRef.current);
                resizeFrameRef.current = null;
            }
        };
    }, [scheduleResizeTextarea]);

    useImperativeHandle(ref, () => {
        return {
            focus: () => {
                setIsEditable(true);
                textareaRef.current.focus();
            },
        };
    });

    return (
        <Container ref={containerRef} onClick={(e) => setIsEditable(true)}>
            <Txtarea
                ref={textareaRef}
                $isEditable={isEditable}
                readOnly={!isEditable}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                rows={1}
                fontsize={props.fontsize}
                fontweight={props.fontweight}
                $padding={props.padding}
                $maxWidth={props.maxWidth}
                name={props.name}
                onChange={props.onChange}
                placeholder={props.placeholder}
                value={props.value}
                disabled={props.disabled}
            />
        </Container>
    );
}

export default React.forwardRef(Textarea);
